import { useForm } from './Dialog.h'
import { useUser } from '@shared/hooks/user'

import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { InfiniteList } from '@shared/components/InfiniteList'
import FormTextField from '@shared/components/FormTextField'
import MotionDialog from '@shared/components/MotionDialog'
import { Controller, FormProvider } from 'react-hook-form'
import SignatureField from '@shared/components/SignatureField'
import { Fragment } from 'react'
import InfoIcon from '@mui/icons-material/Info'
import LinearProgress from '@mui/material/LinearProgress'
import EstimationItem from '@shared/components/estimations/Item'
import { allowedPermissions } from '@shared/utils/allowedPermissions'
import since from '@shared/utils/since'
import Divider from '@mui/material/Divider'

export default function ProfileDialog({props}) {
    const {
        form,
        mutate,
        isPending,
        close,

        tab,
        setTab,

        permissions,
        setPermissions,

        manager
    } = useForm(props)
    const user = useUser()

    return (
        <FormProvider {...form}>
            <MotionDialog
                onClose={() => close('user')}
                slotProps={{
                    paper: {
                        component: 'form',
                        onSubmit: form.handleSubmit(mutate),
                        sx: {height: '100%'}
                    }
                }}
            >
                <DialogTitle>{'instance' in props
                    ? `ملف ${props.instance.first_name}`
                    : 'إضافة مستخدم'
                }</DialogTitle>
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                >
                    <Tab
                        value='data'
                        label='البيانات'
                    />
                    <Tab
                        value='permissions'
                        label='الصلاحيات'
                    />
                    {'instance' in props &&
                        <Tab
                            value='estimations'
                            label={`المقايسات (${props.instance.estimations_count})`}
                        />
                    }
                </Tabs>
                <Divider />
                <DialogContent sx={{p: 0}}>
                    <Box sx={{
                        display: tab == 'data' ? 'block' : 'none',
                        p: 3
                    }}>
                        {'instance' in props &&
                            <>
                                <Typography
                                    color='textSecondary'
                                    variant='body2'
                                >تاريخ الإنضمام: {since(props.instance.date_joined)}</Typography>
                                {!!since(props.instance.last_activity) &&
                                    <Typography
                                        color='textSecondary'
                                        variant='body2'
                                    >آخر نشاط: {since(props.instance.last_activity)}</Typography>
                                }
                            </>
                        }
                        <FormTextField
                            name='username'
                            label='الرقم الوظيفي *'
                        />
                        <Box sx={{
                            display: 'flex',
                            gap: 2
                        }}>
                            <FormTextField
                                name='first_name'
                                label='الاسم الأول *'
                            />
                            <FormTextField
                                name='last_name'
                                label='الاسم الأخير *'
                            />
                        </Box>
                        {!('instance' in props)
                            ? (
                                <FormTextField
                                    name='password'
                                    type='password'
                                    label='كلمة المرور *'
                                />
                            ) : (
                                <FormTextField
                                    name='password'
                                    type='password'
                                    label='كلمة المرور الجديدة'
                                    helperText='دعها فارغة إن لم ترد تغييرها.'
                                />
                            )
                        }
                        <Box sx={{
                            display: 'flex',
                            gap: 2
                        }}>
                            <FormTextField
                                name='email'
                                label='البريد الإلكتروني'
                            />
                            <FormTextField
                                name='phone_number'
                                label='رقم الهاتف'
                            />
                        </Box>
                        {(!props?.instance?.is_main_user && props?.instance?.username != user.username) &&
                            <Controller
                                name='is_active'
                                control={form.control}
                                defaultValue={true}
                                render={({ field }) => 
                                    <FormControlLabel
                                        sx={{my: 2}}
                                        control={
                                            <Checkbox {...field} checked={field.value} />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant='body1'>حساب نشط</Typography>
                                                <Typography
                                                    variant='caption'
                                                    color='text.secondary'
                                                >عدم تفعيله يوقف الحساب، خيار افضل من الحذف.</Typography>
                                            </Box>
                                        }
                                    />
                                }
                            />
                        }
                        <SignatureField
                            label='التوقيع'
                            name='signature'
                        />
                    </Box>
                    <Box sx={{
                        display: tab == 'permissions' ? 'block' : 'none',
                        p: 3
                    }}>
                        {props?.instance?.username == user.username &&
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 3
                            }}>
                                <InfoIcon color='action' />
                                <Typography>لا يمكنك تعديل الصلاحيات.</Typography>
                            </Box>
                        }
                        {props?.instance?.is_main_user &&
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 3
                            }}>
                                <InfoIcon color='action' />
                                <Typography>انت المستخدم الرئيسي، لا يمكنك تعديل صلاحياتك.</Typography>
                            </Box>
                        }
                        {allowedPermissions.map(parent => (
                            <Fragment key={parent.text}>
                                <Typography
                                    sx={{mt: 2}}
                                >{parent.text}</Typography>
                                <Stack
                                    sx={{
                                        borderLeft: '1px solid',
                                        borderColor: 'divider',
                                        pt: 1,
                                        pl: 1,
                                        ml: 2
                                    }}
                                    spacing={.5}
                                >
                                    {parent.permissions.map(permission => (
                                        <FormControlLabel
                                            key={permission.codename}
                                            label={permission.text}
                                            control={
                                                <Checkbox
                                                    disabled={(
                                                        props?.instance?.is_main_user
                                                        || user.username == props?.instance?.username
                                                    )}
                                                    onClick={() => {
                                                        if (permissions.includes(permission.codename)) {
                                                            setPermissions(prev => prev.filter(p => p !== permission.codename))
                                                        } else {
                                                            setPermissions(prev => [...prev, permission.codename])
                                                        }
                                                    }}
                                                    checked={permissions.includes(permission.codename)}
                                                />
                                            }
                                        />
                                    ))}
                                </Stack>
                            </Fragment>
                        ))}
                    </Box>
                    {'instance' in props &&
                        <Box sx={{
                            display: tab == 'estimations' ? 'block' : 'none',
                            height: '100%'
                        }}>
                            {manager.isLoading && <LinearProgress />}
                            {manager.error && <Typography className='centerized'>حدث خطأ</Typography>}
                            <InfiniteList manager={manager}>
                                {manager.visibleItems.map(({item, options}) =>
                                    <EstimationItem
                                        key={item.id}
                                        item={item}
                                        options={options}
                                        infiniteScroll
                                    />
                                )}
                            </InfiniteList>
                        </Box>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='text'
                        onClick={() => close('user')}
                        disabled={isPending}
                    >إغلاق</Button>
                    <Button
                        variant='text'
                        type='submit'
                        disabled={isPending}
                    >حفظ</Button>
                </DialogActions>
            </MotionDialog>
        </FormProvider>
    )
}